// @ts-strict-ignore
import isNil from 'lodash/isNil';
import sortBy from 'lodash/sortBy';
import {AttrsSchema, Component} from 'panel';

import {reporter} from 'common/report/error-reporting';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {addToast} from 'common/components/toast-area/toast-manager';
import {dataGroupIDsEqual} from 'common/util';
import {useStore} from 'common/util/component-helpers';
import {IronContextRegistry} from 'common/report/context-registry';

import {TrackingEvent, TrackingEventPropertyName} from './util/tracking';
import {DataGroup, DataGroupSelectorState, DataGroupType, SelectWarning} from './types';
import {template} from './template';
import css from './index.inline.styl';

export const DEFAULT_CHANGED_ALERT = `Incompatible custom events, properties, or cohorts were removed`;

// todo: i18n make displayName translatable
export const DEFAULT_DATA_GROUP: DataGroup = {
  displayName: `User`,
  groupID: null,
  isReady: true,
  propertyName: ``,
  type: DataGroupType.DEFAULT,
};

export const SYNCED_DATA_GROUPS_EVENT = `syncedDataGroups`;

/**
 * @event DataGroupSelector#selectDataGroup
 * @property {Object} detail.dataGroup
 * @property {string} detail.dataGroup.name Name of the selected data group
 * @property {string} detail.dataGroup.id ID of the selected data group
 *
 * @event DataGroupSelector#syncedDataGroups
 * @property {DataGroup[]} detail.dataGroups
 * @property {DataGroup} detail.selected
 */

/**
 * A styled block containing a dropdown which fetches available data groups and displays them.
 *
 * This is a controlled component, meaning the caller must catch the change
 * event and update the selected-data-group-id attribute to display the new selection.
 *
 * @fires DataGroupSelector#selectDataGroup when the user selects a different data group
 * @fires DataGroupSelector#syncedDataGroups when groups are first loaded and when the group that is selected changes
 */
export class DataGroupSelector extends Component<DataGroupSelectorState, any, unknown, unknown, IronContextRegistry> {
  static get attrsSchema(): AttrsSchema<any> {
    return {
      ...super.attrsSchema,
      'project-id': {type: `number`, description: `project-id necessary to fetch data groups`},
      'selected-data-group-id': {type: `string`, description: `forces the selected group`},
      'show-user-group-only': {type: `boolean`, description: `show the user group only`},
      'change-warning': {type: `boolean`, description: `Deprecated. Please use select-warnings`},
      'select-warnings': {type: `json`, default: []},
      theme: {type: `string`, enum: [``, `builder`]},
      'workspace-id': {type: `number`},
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        dataGroups: null,
        fetchedDataGroups: false,
        selected: DEFAULT_DATA_GROUP,
        optionsMenuOpen: false,
      },
      contexts: [`i18n` as const],
      helpers: {
        getDataGroupsList: () => {
          const {dataGroups, selected} = this.state;
          const items = (dataGroups || []).map((dataGroup) => ({
            isDisabled: !dataGroup.isReady,
            isSelected: selected.groupID === dataGroup.groupID,
            label:
              dataGroup.displayName === `User`
                ? this.getContext(`i18n`).t(`query-builder.dg-user`)
                : dataGroup.displayName,
            tooltip: this.getWarningTooltip({dataGroup, selectedDataGroup: selected}),
            value: dataGroup.groupID,
          }));
          return [{items, isLoading: isNil(dataGroups)}];
        },
        clickDataGroupOption: (ev) => {
          const dataGroupItem = ev.detail.item;
          const {selected} = this.state;
          const changed = selected.groupID !== dataGroupItem.value;
          if (!changed) {
            return;
          }

          const changedWarning = this.getChangedAlert(dataGroupItem.value);
          if (changedWarning) {
            addToast({message: changedWarning});
          }

          this.dispatchEvent(
            new CustomEvent(`selectDataGroup`, {
              bubbles: true,
              detail: {
                dataGroup: {
                  name: dataGroupItem.label,
                  id: dataGroupItem.value,
                },
              },
            }),
          );

          this.dispatchEvent(
            new CustomEvent(`track`, {
              bubbles: true,
              composed: true,
              detail: {
                eventName: TrackingEvent.GroupSelected,
                props: {
                  [TrackingEventPropertyName.CurrentDataGroupName]: selected.displayName,
                  [TrackingEventPropertyName.CurrentDataGroupId]: selected.groupID,
                  [TrackingEventPropertyName.NewDataGroupName]: dataGroupItem.label,
                  [TrackingEventPropertyName.NewDataGroupId]: dataGroupItem.value,
                },
              },
            }),
          );

          this.update({optionsMenuOpen: false});
        },
      },
    };
  }

  /**
   * Allow passing in data groups to render, either for mocking or to avoid unnecessary fetching
   */
  set dataGroups(dataGroups) {
    this.processDataGroups(dataGroups);
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.attr(`show-user-group-only`)) {
      this.update({fetchedDataGroups: true, selected: DEFAULT_DATA_GROUP, dataGroups: [DEFAULT_DATA_GROUP]});
    } else {
      (async () => {
        try {
          await useStore(this, dataGroupStore, () => {
            this.processDataGroups(Object.values(dataGroupStore.value));
          }).fetch({
            projectId: this.attr(`project-id`),
            workspaceId: this.attr(`workspace-id`),
          });
        } catch (err) {
          this.update({fetchedDataGroups: true, dataGroups: [DEFAULT_DATA_GROUP]});
          reporter.error(err);
        }
      })();
    }
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (attr === `selected-data-group-id` && this.state.dataGroups) {
      const groupID = this.attr(attr);
      const selected = this.state.dataGroups.find((dataGroup) => dataGroup.groupID === groupID) || DEFAULT_DATA_GROUP;
      this.update({selected});
      this.dispatchSyncedEvent(this.state.dataGroups, selected);
    }
  }

  getWarningTooltip({
    dataGroup,
    selectedDataGroup,
  }: {
    dataGroup: DataGroup;
    selectedDataGroup: DataGroup;
  }): {text: string} | null {
    const isSelected = dataGroup.groupID === selectedDataGroup.groupID;
    if (isSelected) {
      return null;
    }

    if (this.attr(`change-warning`)) {
      return {text: this.defaultWarningText({selectedDataGroup})};
    }
    const selectWarnings: SelectWarning[] = this.attr(`select-warnings`);
    const selectWarning = selectWarnings.find((warning) => dataGroupIDsEqual(warning.dataGroupId, dataGroup.groupID));
    if (selectWarning && selectWarning.showWarning) {
      const warningText = (selectWarning && selectWarning.warning) || this.defaultWarningText({selectedDataGroup});
      return {text: warningText};
    }
    return null;
  }

  defaultWarningText({selectedDataGroup}: {selectedDataGroup: DataGroup}): string {
    if (!selectedDataGroup.groupID) {
      // User data group is selected
      return `${selectedDataGroup.displayName} property and Cohort conditions will be removed`;
    }
    return `${selectedDataGroup.displayName} Cohort conditions will be removed`;
  }

  getChangedAlert(dataGroupId: string) {
    if (this.attr(`change-warning`)) {
      return DEFAULT_CHANGED_ALERT;
    }
    const selectWarnings: SelectWarning[] = this.attr(`select-warnings`);
    const selectWarning = selectWarnings.find((warning) => dataGroupIDsEqual(warning.dataGroupId, dataGroupId));
    if (selectWarning && selectWarning.showWarning) {
      return selectWarning.changedAlert || DEFAULT_CHANGED_ALERT;
    }
    return null;
  }

  dispatchSyncedEvent(dataGroups: DataGroup[], selected: DataGroup) {
    this.dispatchEvent(new CustomEvent(SYNCED_DATA_GROUPS_EVENT, {bubbles: true, detail: {selected, dataGroups}}));
  }

  processDataGroups(dataGroups) {
    const allGroups = [DEFAULT_DATA_GROUP, ...sortBy(dataGroups, (dataGroup) => dataGroup.displayName)];
    const groupID = this.attr(`selected-data-group-id`);
    let selected = this.state.selected;
    if (groupID) {
      selected = allGroups.find((dataGroup) => dataGroup.groupID === groupID) || selected;
    }
    this.update({dataGroups: allGroups, selected, fetchedDataGroups: true});

    this.dispatchSyncedEvent(allGroups, selected);
  }

  /**
   * Exposes dataGroups from this component's state to simplify retrieval (especially for legacy backbone code)
   */
  getDataGroups(): null | DataGroup[] {
    return this.state.dataGroups;
  }

  /**
   * Exposes selected dataGroup from this component's state
   */
  getSelected(): DataGroup {
    return this.state.selected;
  }
}

type JsxProps = {
  attrs?: any;
  on?: any;
};

export const DataGroupSelectorEl = defineAndMakeJsxFactory<JsxProps>(`mp-data-group-selector`, DataGroupSelector);
export const DataGroupSelectorReact = panel2react<JsxProps>(`mp-data-group-selector`, DataGroupSelector);
